import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthDispatch } from "context/Auth";
import app, { Realm } from "./realm";

// Helper — build auth state user from Realm currentUser + freshly refreshed customData
const buildUser = async (realmUser) => {
  await realmUser.refreshCustomData();
  const custom = realmUser.customData || {};
  return {
    userId: custom.email || realmUser.profile?.email || "",
    subscriptionType: custom.subscriptionType || "limited",
    fullName: custom.fullName || "",
    token: "realm",
    refresh: null,
  };
};

// ── Auth ──────────────────────────────────────────────────────────────────────

export const LoginUser = (setErrors) => {
  const dispatch = useAuthDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const loginUser = async ({ variables }) => {
    const { email, password } = variables;
    setLoading(true);
    try {
      const credentials = Realm.Credentials.emailPassword(email, password);
      const realmUser = await app.logIn(credentials);
      const user = await buildUser(realmUser);
      dispatch({ type: "LOGIN", payload: user });
      navigate("/dashboard");
    } catch (err) {
      setErrors({ general: err.message || "Login failed" });
    } finally {
      setLoading(false);
    }
  };

  return { loginUser, loading };
};

export const RegisterUser = (setErrors) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const registerUser = async ({ variables }) => {
    const { email, password, fullName, termscondition } = variables;
    const errors = {};

    if (!fullName || fullName.trim() === "") errors.name = "Full name must not be empty";
    if (!email || email.trim() === "") errors.email = "Email must not be empty";
    if (!password || password.trim() === "") errors.password = "Password must not be empty";
    if (termscondition !== true) errors.termscondition = "Terms and conditions must be accepted";
    if (Object.keys(errors).length > 0) { setErrors(errors); return; }

    setLoading(true);
    try {
      // 1. Create Realm email/password user
      await app.emailPasswordAuth.registerUser({ email, password });

      // 2. Log in immediately to get a current user
      const credentials = Realm.Credentials.emailPassword(email, password);
      const realmUser = await app.logIn(credentials);

      // 3. Call Atlas Function to create Stripe customer + MongoDB User doc
      await realmUser.callFunction("registerUser", { fullName, email });

      await app.currentUser.logOut();
      navigate("/authentication/sign-in", { replace: true });
    } catch (err) {
      if (err.message?.includes("name already in use")) {
        setErrors({ email: "Email is already taken" });
      } else {
        setErrors({ general: err.message || "Registration failed" });
      }
    } finally {
      setLoading(false);
    }
  };

  return { registerUser, loading };
};

// Stubs kept for Auth context compatibility — Realm manages token refresh internally
export const RefreshUser = (setTokens) => {
  const refreshUser = async () => {};
  return { refreshUser, loading: false };
};

export const SingleRefreshUser = (setToken) => {
  const singleRefreshUser = async () => {};
  return { singleRefreshUser, loading: false };
};

export const GetReset = (setInfo) => {
  const [resetLoading, setLoading] = React.useState(false);

  const getReset = async ({ variables }) => {
    const { email } = variables;
    setLoading(true);
    try {
      await app.emailPasswordAuth.sendResetPasswordEmail({ email });
      // Simulate the shape the ResetPass component checks
      setInfo(JSON.stringify({ accepted: [email] }));
    } catch (err) {
      setInfo({ email: err.message || "Could not send reset email" });
    } finally {
      setLoading(false);
    }
  };

  return { getReset, resetLoading };
};

export const ResetPassword = (setErrors) => {
  const navigate = useNavigate();
  const [rpLoading, setLoading] = React.useState(false);

  const resetPassword = async ({ variables }) => {
    const { token, tokenId, password } = variables;
    setLoading(true);
    try {
      await app.emailPasswordAuth.resetPassword({ token, tokenId, password });
      alert("Password was successfully changed.");
      navigate("/authentication/sign-in", { replace: true });
    } catch (err) {
      setErrors({ token: err.message || "Reset link expired or invalid" });
    } finally {
      setLoading(false);
    }
  };

  return { resetPassword, rpLoading };
};

// ── User / Billing ────────────────────────────────────────────────────────────

export const CheckPayment = (setErrors) => {
  const dispatch = useAuthDispatch();
  const navigate = useNavigate();
  const [paymentLoading, setLoading] = React.useState(false);

  const checkPayment = async () => {
    setLoading(true);
    try {
      const realmUser = app.currentUser;
      if (!realmUser) throw new Error("Not logged in");
      await realmUser.callFunction("checkPayment");
      const user = await buildUser(realmUser);
      dispatch({ type: "LOGIN", payload: user });
      navigate("/dashboard");
    } catch (err) {
      if (setErrors) setErrors({ general: err.message });
    } finally {
      setLoading(false);
    }
  };

  return { checkPayment, paymentLoading };
};

export const StripePayment = () => {
  const [stripeLoading, setLoading] = React.useState(false);

  const stripePayment = async () => {
    setLoading(true);
    try {
      const url = await app.currentUser.callFunction("createStripeSession");
      window.location.href = url;
    } catch (err) {
      // handle silently
    } finally {
      setLoading(false);
    }
  };

  return { stripePayment, stripeLoading };
};

export const RemoveSubscription = (setErrors) => {
  const dispatch = useAuthDispatch();
  const navigate = useNavigate();
  const [removeLoading, setLoading] = React.useState(false);

  const removeSubscription = async () => {
    setLoading(true);
    try {
      await app.currentUser.callFunction("cancelSubscription");
      const user = await buildUser(app.currentUser);
      dispatch({ type: "LOGIN", payload: user });
      navigate("/");
    } catch (err) {
      if (setErrors) setErrors({ general: err.message });
    } finally {
      setLoading(false);
    }
  };

  return { removeSubscription, removeLoading };
};

export const GetInvoices = (setInfo) => {
  const [invoicesLoading, setLoading] = React.useState(false);

  const getInvoices = async () => {
    setLoading(true);
    try {
      const url = await app.currentUser.callFunction("getBillingPortal");
      setInfo(url);
    } catch (err) {
      if (setInfo) setInfo("");
    } finally {
      setLoading(false);
    }
  };

  return { getInvoices, invoicesLoading };
};

export const GetUser = (setErrors) => {
  const [userLoading, setLoading] = React.useState(false);
  const [userData, setData] = React.useState(null);

  React.useEffect(() => {
    const fetch = async () => {
      if (!app.currentUser) return;
      setLoading(true);
      try {
        const mongodb = app.currentUser.mongoClient("mongodb-atlas");
        const users = mongodb.db("writeai").collection("users");
        const user = await users.findOne({ realmUserId: app.currentUser.id });
        setData(user);
      } catch (err) {
        if (setErrors) setErrors({ general: err.message });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return { userData, userLoading };
};

export const SetUser = (setInfo) => {
  const [setUserLoading, setLoading] = React.useState(false);

  const setUser = async ({ variables }) => {
    setLoading(true);
    try {
      const mongodb = app.currentUser.mongoClient("mongodb-atlas");
      const users = mongodb.db("writeai").collection("users");
      const update = { ...variables, updatedAt: new Date() };
      Object.keys(update).forEach((k) => update[k] === undefined && delete update[k]);
      await users.updateOne(
        { realmUserId: app.currentUser.id },
        { $set: update }
      );
      if (setInfo) setInfo("Saved");
    } catch (err) {
      if (setInfo) setInfo({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return { setUser, setUserLoading };
};

export const GetWrite = (setInfo) => {
  const [writeLoading, setLoading] = React.useState(false);

  const getWrite = async ({ variables }) => {
    setLoading(true);
    try {
      const text = await app.currentUser.callFunction("getWrite", variables);
      setInfo(text);
    } catch (err) {
      if (setInfo) setInfo("");
    } finally {
      setLoading(false);
    }
  };

  return { getWrite, writeLoading };
};
